import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';
import { logout } from './authSlice';

// Patient Registration
export const patientRegistration = createAsyncThunk(
    'patient/patientRegistration',
    async ({ name, surname, email, password, confirmPassword, countryId, timeZone, gender, dob, invitationCode, emailVerified }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const payload = {
                name,
                surname,
                email,
                password,
                confirmPassword,
                countryId,
                timeZone,
                gender,
                dob,
                emailVerified,
            };

            // Build URL with invitationCode as query parameter if it exists
            let url = `${baseURL}/patients/register`;
            if (invitationCode) {
                url += `?invitationCode=${encodeURIComponent(invitationCode)}`;
            }

            const response = await axiosInstance.post(url, payload);
            const jsonResponse = response.data;
            return jsonResponse;
        } catch (error) {
            return rejectWithValue({ error });
        }
    }
);

// ==================== PATIENT BULK UPLOAD ====================
// Mirrors the Country/Support bulk pattern (download sample → check format →
// bulk upload). Doctors pass their own doctorId so every row links to them;
// admins omit it and use the per-row DoctorEmail column instead.

export const downloadPatientSampleExcel = createAsyncThunk(
    'patient/downloadPatientSampleExcel',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/patients/download-sample-excel`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sample_patient_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return { success: true };
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to download patient sample'
            });
        }
    }
);

export const checkPatientBulkFormat = createAsyncThunk(
    'patient/checkPatientBulkFormat',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/patients/check-bulk-format`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                valid: false,
                error: error.response?.data?.message || error.message || 'Invalid Excel format',
                errors: error.response?.data?.errors
            });
        }
    }
);

export const bulkUploadPatients = createAsyncThunk(
    'patient/bulkUploadPatients',
    async ({ file, doctorId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (doctorId) formData.append('doctorId', doctorId);
            const response = await axiosInstance.post(`${baseURL}/patients/bulk-upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to bulk upload patients'
            });
        }
    }
);

// Fetch all patients
export const getPatients = createAsyncThunk(
    'patient/getPatients',
    async (
        {
            page = 1,
            limit = 5,
            search = '',
            ageStart = '',
            ageEnd = '',
            countryId = '',
            gender = '',
            profileStatus = '',
            status = ''
        } = {},
        { rejectWithValue }
    ) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ page, limit });
            if (search) params.append('search', search);
            if (ageStart) params.append('ageStart', ageStart);
            if (ageEnd) params.append('ageEnd', ageEnd);
            if (countryId) params.append('countryId', countryId);
            if (gender) params.append('gender', gender);
            if (profileStatus) params.append('profileStatus', profileStatus);
            if (status) params.append('status', status);
            const response = await axiosInstance.get(`${baseURL}/patients?${params.toString()}`);
            const jsonResponse = response.data;
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Fetch patients by doctor
// thunk
export const getDoctorPatients = createAsyncThunk(
    'patient/getDoctorPatients',
    async (
        {
            doctorId,
            page = 1,
            limit = 5,
            search = '',
            ageStart = '',
            ageEnd = '',
            countryId = '',
            gender = '',
            profileStatus = ''
        },
        { rejectWithValue }
    ) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ page, limit });
            if (search) params.append('search', search);
            if (ageStart) params.append('ageStart', ageStart);
            if (ageEnd) params.append('ageEnd', ageEnd);
            if (countryId) params.append('countryId', countryId);
            if (gender) params.append('gender', gender);
            if (profileStatus) params.append('profileStatus', profileStatus);
            const response = await axiosInstance.get(
                `${baseURL}/patients/byDoctor/${doctorId}?${params.toString()}`
            );
            return response.data; // returns { patients, total, page, limit, totalPages }
        } catch (error) {
            return rejectWithValue({
                error
            });
        }
    }
);

// Get Patient Name List for Selection
export const getPatienttsNameList = createAsyncThunk(
    'patient/getPatienttsNameList',
    async (doctorId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/patients/select/patientsList/${doctorId}`);
            return response.data.data; // returns array of { name, patientId }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch patients list';
            return rejectWithValue({ error: errorMessage });
        }
    }
);

// Get All Patients List
export const getAllPatientsList = createAsyncThunk(
    'patient/getAllPatientsList',
    async ({ search = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            const response = await axiosInstance.get(`${baseURL}/patients/select/patientsList?${params.toString()}`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch patients list';
            return rejectWithValue({ error: errorMessage });
        }
    }
);

// Fetch patient by ID
export const getPatientById = createAsyncThunk(
    'patient/getPatientById',
    async (patientid, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        const userRole = localStorage.getItem('Role');
        const lang = localStorage.getItem('i18nextLng') || 'en';
        try {
            const response = await axiosInstance.get(`${baseURL}/patients/${patientid}`);
            if (userRole === 'patient' && response.data.status === 'inactive') {
                dispatch(logout({ lang }));
                window.location.href = '/login';
                return rejectWithValue({ message: 'Patient account is inactive', status: 403 });
            }
            return response.data;
        } catch (error) {
            // Extract only serializable error properties
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch patient';
            const errorStatus = error?.response?.status || null;
            return rejectWithValue({
                message: errorMessage,
                status: errorStatus,
                code: error?.code || null
            });
        }
    }
);

// Add a patient
export const addPatient = createAsyncThunk(
    'patient/addPatient',
    async (payload, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        const userRole = localStorage.getItem('Role');
        try {
            const response = await axiosInstance.post(`${baseURL}/patients`, payload);
            const jsonResponse = response.data;
            if (userRole === 'admin') {
                dispatch(getPatients({ page: 1, limit: 5 }));
            }
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Update a patient
export const updatePatient = createAsyncThunk(
    'patient/updatePatient',
    async ({ patientid, payload, keypass }, { dispatch, rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/patients/${patientid}`, payload);
            const jsonResponse = response.data;
            dispatch(getPatientById(patientid));
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Delete a patient
export const deletePatient = createAsyncThunk(
    'patient/deletePatient',
    async (patientid, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/patients/${patientid}`);
            const jsonResponse = response.data;
            return { patientid };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// ✅ Update Patient Status
export const updatePatientStatus = createAsyncThunk(
    'patient/updatePatientStatus',
    async ({ patientId, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/patients/update-status`, {
                patientId,
                status
            });
            const jsonResponse = response.data;
            return jsonResponse;
        } catch (error) {
            return rejectWithValue({ error });
        }
    }
);

// Create custom price
export const createCustomPrice = createAsyncThunk(
    'patient/createCustomPrice',
    async ({ patientid, payload }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/patients/customprice/${patientid}`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Delete custom price
export const deleteCustomPrice = createAsyncThunk(
    'patient/deleteCustomPrice',
    async ({ patientid, spId, atId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            await axiosInstance.delete(`${baseURL}/patients/customprice/${patientid}/${spId}/${atId}`);
            return { patientid, spId, atId };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Upload Patient File
export const uploadPatientFile = createAsyncThunk(
    "patient/uploadPatientFile",
    async ({ fileData, obj }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(
                `${baseURL}/appointment/fileuploadtool/null/${obj.patientId}`,
                fileData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            const jsonResponse = response.data;
            dispatch(getAllPatientFile({ docId: obj.docId, patientId: obj.patientId, date: 'null' }));
            return jsonResponse;
        } catch (error) {
            return rejectWithValue({ error });
        }
    }
);

// Get All Patient Files
export const getAllPatientFile = createAsyncThunk(
    'patient/getAllPatientFile',
    async ({ docId, patientId, date, page = 1, limit = 5 }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/appointment/toolfileview/null/${patientId}/null?page=${page}&limit=${limit}`
            );
            return response?.data; // Expected: { data: [...], pagination: {...}, message: {...} }
        } catch (error) {
            // Handle "No files found" gracefully – return empty result instead of rejecting
            if (error?.response?.data?.message?.en === 'No files found') {
                return {
                    data: [],
                    pagination: { totalFiles: 0, limit, page, totalPages: 0 },
                    message: error.response.data.message,
                };
            }
            // For any other error, reject with value
            return rejectWithValue({ error: error?.response?.data || error.message });
        }
    }
);

// Replace Toolbox File
export const replaceToolboxFile = createAsyncThunk(
    'patient/replaceToolboxFile',
    async ({ fileData, fileId, obj }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/appointment/replacefile/toolbox/${fileId}`, fileData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            const jsonResponse = response.data;
            dispatch(getAllPatientFile({ docId: obj.docId, patientId: obj.patientId, date: 'null' }));
            return jsonResponse;
        } catch (error) {
            return rejectWithValue({ error });
        }
    }
);

// Delete Patient File
export const deletePatientFile = createAsyncThunk(
    'patient/deletePatientFile',
    async ({ obj, fileId }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/appointment/deletefile/toolbox/${fileId}`);
            const jsonResponse = response.data;
            dispatch(getAllPatientFile({ docId: obj.docId, patientId: obj.patientId, date: 'null' }));
            return jsonResponse;
        } catch (error) {
            return rejectWithValue({ error });
        }
    }
);

export const patientProfileUpdate = createAsyncThunk(
    'patient/profileUpdate',
    async ({ patientId, formData }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(
                `${baseURL}/patients/profile/upload/${patientId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            dispatch(getPatientById(patientId))
            const jsonResponse = response.data;
            return jsonResponse;
        } catch (error) {
            return rejectWithValue({ error });
        }
    }
);

export const removePatientProfileImage = createAsyncThunk(
    'patient/removePatientProfileImage',
    async (patientId, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(
                `${baseURL}/patients/profile/remove/${patientId}`
            );
            const jsonResponse = response.data;
            dispatch(getPatientById(patientId));
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Get Medical History by Patient
export const getMedicalHistory = createAsyncThunk(
    'patient/getMedicalHistory',
    async ({ patientId, status, page = 1, limit = 5 }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const userRole = localStorage.getItem('Role')
            const params = new URLSearchParams();
            if (patientId) params.append('patientId', patientId);
            if (userRole === 'doctor') params.append('doctorId', localStorage.getItem('ID'));
            if (status) params.append('status', status);
            if (page) params.append('page', page);
            if (limit) params.append('limit', limit);
            const response = await axiosInstance.get(
                `${baseURL}/appointment/getMedicalHistoryByPatient?${params.toString()}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getAllChatDoctor = createAsyncThunk(
    'doctor/viewDoctorList',
    async ({ patientId }, { rejectWithValue }) => {
        console.log("slice", patientId);
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/patients/select/doctor/${patientId}`);
            const jsonResponse = response.data;
            return jsonResponse;
        } catch (error) {
            return rejectWithValue({ error });
        }
    }
)

// Get My Doctors (for patient to see their doctors)
export const getMyDoctors = createAsyncThunk(
    'patient/getMyDoctors',
    async (
        {
            patientId,
            page = 1,
            limit = 10,
            search = '',
            gender = '',
            countryId = ''
        },
        { rejectWithValue }
    ) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ page, limit });
            if (search) params.append('search', search);
            if (gender) params.append('gender', gender);
            if (countryId) params.append('countryId', countryId);
            const response = await axiosInstance.get(`${baseURL}/patients/${patientId}/doctors?${params.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Get Patients Library (for library page - all patients)
export const getPatientsLibrary = createAsyncThunk(
    'patient/getPatientsLibrary',
    async (
        {
            page = 1,
            limit = 5,
            search = '',
            gender = '',
            countryId = '',
            doctorId = ''
        } = {},
        { rejectWithValue }
    ) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ page, limit });
            if (search) params.append('search', search);
            if (gender) params.append('gender', gender);
            if (countryId) params.append('countryId', countryId);
            if (doctorId) params.append('doctorId', doctorId);
            const response = await axiosInstance.get(`${baseURL}/patients/all?${params.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Lookup Patient By Email
export const lookupPatientByEmail = createAsyncThunk(
    'patient/lookupPatientByEmail',
    async ({ email, axiosInstance }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/patients/lookup-by-email?email=${email.trim()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Slice
const patientSlice = createSlice({
    name: 'patient',
    initialState: {
        patients: [],
        docPatients: [],
        patientNameList: [], // List of patients for selection dropdown
        allPatientsList: [], // List of all patients for selection (from getAllPatientsList)
        selectedPatient: null,
        isLoading: false,
        patientsLoading: false,
        paginationfiles: null,
        error: null,
        success: false,
        doctorChatList: [],
        medicalHistory: null,
        medicalHistoryData: [],
        medicalHistoryPage: 1,
        medicalHistoryTotalPages: 0,
        medicalHistoryTotalItems: 0,
        medicalHistoryLoading: false,
        // Pagination metadata for admin
        total: 0,
        page: 1,
        limit: 5,
        totalPages: 0,
        // Pagination metadata for doctor
        docTotal: 0,
        docPage: 1,
        docLimit: 5,
        docTotalPages: 0,
        // My Doctors (for patient)
        myDoctors: [],
        myDoctorsLoading: false,
        myDoctorsTotal: 0,
        myDoctorsPage: 1,
        myDoctorsLimit: 10,
        myDoctorsTotalPages: 0,
        // Patients Library
        patientsLibrary: [],
        patientsLibraryLoading: false,
        patientsLibraryTotal: 0,
        patientsLibraryPage: 1,
        patientsLibraryLimit: 5,
        patientsLibraryTotalPages: 0,
    },
    reducers: {
        updatePatientChat: (state, action) => {
            const updatedChat = action.payload;

            // Handle both chatListUpdate structure (with nested doctor) and direct doctor structure
            const doctorId = updatedChat.doctorId || updatedChat.doctor?.doctorId;
            const doctorData = updatedChat.doctor || updatedChat;

            const index = state.doctorChatList.findIndex(
                d => d.doctorId === doctorId
            );

            const incomingMsg = updatedChat.lastMessage;
            const incomingId = incomingMsg?._id;

            // Use unreadForPatient for patient's view (or fallback to unread)
            const unreadCount = updatedChat.unreadForPatient !== undefined
                ? updatedChat.unreadForPatient
                : updatedChat.unread || 0;

            // Case 1: Doctor exists
            if (index !== -1) {
                const doctor = state.doctorChatList[index];

                const existingMsg = doctor.lastMessage;
                const existingId = existingMsg?._id;

                // ✨ If no NEW message, still update unread count and other fields
                if (existingId === incomingId) {
                    state.doctorChatList[index] = {
                        ...doctor,
                        ...doctorData,
                        unviewedCount: unreadCount,
                        unread: unreadCount,
                        unreadForPatient: unreadCount,
                    };
                    return;
                }

                // Replace the message and update doctor data
                state.doctorChatList[index] = {
                    ...doctor,
                    ...doctorData,
                    lastMessage: incomingMsg,
                    unviewedCount: unreadCount,
                    unread: unreadCount,
                    unreadForPatient: unreadCount,
                };
            } else {
                // Case 2: New chat
                state.doctorChatList.push({
                    ...doctorData,
                    doctorId: doctorId,
                    lastMessage: incomingMsg,
                    unviewedCount: unreadCount,
                    unread: unreadCount,
                    unreadForPatient: unreadCount,
                });
            }

            // ✨ After update, sort by valid timestamp
            state.doctorChatList.sort((a, b) => {
                const dateA = a.lastMessage?.createdAt
                    ? new Date(a.lastMessage.createdAt)
                    : 0;

                const dateB = b.lastMessage?.createdAt
                    ? new Date(b.lastMessage.createdAt)
                    : 0;

                return dateB - dateA;
            });
        }
    },
    extraReducers: (builder) => {
        builder
            // Patient Registration
            .addCase(patientRegistration.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(patientRegistration.fulfilled, (state, action) => {
                state.isLoading = false;
                state.success = true;
            })
            .addCase(patientRegistration.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to register patient';
            })
            // Get Patients
            .addCase(getPatients.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getPatients.fulfilled, (state, action) => {
                state.isLoading = false;
                state.patients = action.payload.patients || [];
                state.total = action.payload.total || 0;
                state.page = action.payload.page || 1;
                state.limit = action.payload.limit || 5;
                state.totalPages = action.payload.totalPages || 0;
            })
            .addCase(getPatients.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to fetch patients';
            })
            // Get Doctor Patients
            .addCase(getDoctorPatients.pending, (state) => {
                state.patientsLoading = true;
                state.error = null;
            })
            .addCase(getDoctorPatients.fulfilled, (state, action) => {
                state.patientsLoading = false;
                state.docPatients = action.payload.patients || [];
                state.docTotal = action.payload.total || 0;
                state.docPage = action.payload.page || 1;
                state.docLimit = action.payload.limit || 5;
                state.docTotalPages = action.payload.totalPages || 0;
            })
            .addCase(getDoctorPatients.rejected, (state, action) => {
                state.patientsLoading = false;
                state.error = 'Failed to fetch doctor patients';
            })
            // Get Patient Name List
            .addCase(getPatienttsNameList.pending, (state) => {
                state.patientsLoading = true;
                state.error = null;
            })
            .addCase(getPatienttsNameList.fulfilled, (state, action) => {
                state.patientsLoading = false;
                state.patientNameList = action.payload || [];
            })
            .addCase(getPatienttsNameList.rejected, (state, action) => {
                state.patientsLoading = false;
                state.patientNameList = [];
                state.error = action.payload?.error || 'Failed to fetch patients list';
            })
            // Get All Patients List
            .addCase(getAllPatientsList.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAllPatientsList.fulfilled, (state, action) => {
                state.isLoading = false;
                state.allPatientsList = action.payload?.data || action.payload || [];
            })
            .addCase(getAllPatientsList.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to fetch all patients list';
            })
            // Get Patient By ID
            .addCase(getPatientById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getPatientById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedPatient = action.payload;
            })
            .addCase(getPatientById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || action.payload || 'Failed to fetch patient';
            })
            // Add Patient
            .addCase(addPatient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(addPatient.fulfilled, (state, action) => {
                state.isLoading = false;
                state.patients = [...state.patients, action.payload.data || action.payload];
            })
            .addCase(addPatient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to add patient';
            })
            // Update Patient
            .addCase(updatePatient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updatePatient.fulfilled, (state, action) => {
                state.isLoading = false;
                state.patients = state.patients.map(patient =>
                    patient._id === action.payload.data?._id ? action.payload.data : patient
                );
                state.selectedPatient = action.payload.data || action.payload;
            })
            .addCase(updatePatient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to update patient';
            })
            // Delete Patient
            .addCase(deletePatient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deletePatient.fulfilled, (state, action) => {
                state.isLoading = false;
                const deletedId = action.payload.patientid;
                state.patients = state.patients.filter(p => p._id !== deletedId);
            })
            .addCase(deletePatient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to delete patient';
            })
            // Update Patient Status
            .addCase(updatePatientStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updatePatientStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                state.success = true;
            })
            .addCase(updatePatientStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to update patient status';
            })
            // Create Custom Price
            .addCase(createCustomPrice.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createCustomPrice.fulfilled, (state, action) => {
                state.isLoading = false;
                state.patients = state.patients.map(patient =>
                    patient._id === action.payload.data?._id ? action.payload.data : patient
                );
                state.selectedPatient = action.payload.data || state.selectedPatient;
            })
            .addCase(createCustomPrice.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to create custom price';
            })
            // Delete Custom Price
            .addCase(deleteCustomPrice.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteCustomPrice.fulfilled, (state, action) => {
                state.isLoading = false;
                state.patients = state.patients.map(patient =>
                    patient._id === action.payload.patientid ? { ...patient, customPrice: null } : patient
                );
                if (state.selectedPatient?._id === action.payload.patientid) {
                    state.selectedPatient = { ...state.selectedPatient, customPrice: null };
                }
            })
            .addCase(deleteCustomPrice.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to delete custom price';
            })
            .addCase(uploadPatientFile.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadPatientFile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.success = true;
            })
            .addCase(uploadPatientFile.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to upload file';
            })
            .addCase(getAllPatientFile.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAllPatientFile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.files = action.payload?.data || [];
                state.paginationfiles = action.payload?.pagination || null;
                state.success = true;
            })
            .addCase(getAllPatientFile.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to fetch files';
            })
            .addCase(replaceToolboxFile.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(replaceToolboxFile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.success = true;
            })
            .addCase(replaceToolboxFile.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to replace file';
            })
            .addCase(deletePatientFile.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deletePatientFile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.success = true;
            })
            .addCase(deletePatientFile.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to delete file';
            })
            // patientProfileUpdate cases
            .addCase(patientProfileUpdate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                // state.success = false;
            })
            .addCase(patientProfileUpdate.fulfilled, (state, action) => {
                state.isLoading = false;
                state.admin = action.payload;
                // state.success = true;
            })
            .addCase(patientProfileUpdate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload.error;
                // state.success = false;
            })
            .addCase(removePatientProfileImage.pending, (state) => {
                state.removeLoading = true;
                state.error = null;
            })
            .addCase(removePatientProfileImage.fulfilled, (state, action) => {
                state.removeLoading = false;
                state.success = true;
                // if (state.selectedPatient?._id === action.meta.arg) {
                //     state.selectedPatient = {
                //         ...state.selectedPatient,
                //         profileImage: null
                //     };
                // }
            })
            .addCase(removePatientProfileImage.rejected, (state, action) => {
                state.removeLoading = false;
                state.error = action.payload || 'Failed to remove profile image';
            })
            // Get Medical History
            .addCase(getMedicalHistory.pending, (state) => {
                state.medicalHistoryLoading = true;
                state.error = null;
            })
            .addCase(getMedicalHistory.fulfilled, (state, action) => {
                state.medicalHistoryLoading = false;
                state.medicalHistory = action.payload;
                const currentPage = action.meta.arg?.page || 1;

                if (currentPage === 1) {
                    // First page - replace data
                    state.medicalHistoryData = action.payload.data || [];
                } else {
                    // Subsequent pages - append data
                    state.medicalHistoryData = [
                        ...state.medicalHistoryData,
                        ...(action.payload.data || [])
                    ];
                }

                state.medicalHistoryPage = action.payload.page || currentPage;
                state.medicalHistoryTotalPages = action.payload.totalPages || 0;
                state.medicalHistoryTotalItems = action.payload.totalItems || 0;
            })
            .addCase(getMedicalHistory.rejected, (state, action) => {
                state.medicalHistoryLoading = false;
                state.error = action.payload || 'Failed to fetch medical history';
            })
            // Get Patient List for Chat
            .addCase(getAllChatDoctor.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAllChatDoctor.fulfilled, (state, action) => {
                state.isLoading = false;
                state.doctorChatList = action.payload.data;
                state.success = true;
            })
            .addCase(getAllChatDoctor.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to fetch patient list for chat';
            })
            // Get My Doctors
            .addCase(getMyDoctors.pending, (state) => {
                state.myDoctorsLoading = true;
                state.error = null;
            })
            .addCase(getMyDoctors.fulfilled, (state, action) => {
                state.myDoctorsLoading = false;
                state.myDoctors = action.payload.doctors || [];
                state.myDoctorsTotal = action.payload.total || 0;
                state.myDoctorsPage = action.payload.page || 1;
                state.myDoctorsLimit = action.payload.limit || 10;
                state.myDoctorsTotalPages = action.payload.totalPages || 0;
            })
            .addCase(getMyDoctors.rejected, (state, action) => {
                state.myDoctorsLoading = false;
                state.error = action.payload || 'Failed to fetch my doctors';
            })
            // Get Patients Library
            .addCase(getPatientsLibrary.pending, (state) => {
                state.patientsLibraryLoading = true;
                state.error = null;
            })
            .addCase(getPatientsLibrary.fulfilled, (state, action) => {
                state.patientsLibraryLoading = false;
                state.patientsLibrary = action.payload.patients || [];
                state.patientsLibraryTotal = action.payload.total || 0;
                state.patientsLibraryPage = action.payload.page || 1;
                state.patientsLibraryLimit = action.payload.limit || 5;
                state.patientsLibraryTotalPages = action.payload.totalPages || 0;
            })
            .addCase(getPatientsLibrary.rejected, (state, action) => {
                state.patientsLibraryLoading = false;
                state.error = action.payload || 'Failed to fetch patients library';
            })
            // Lookup Patient By Email
            .addCase(lookupPatientByEmail.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(lookupPatientByEmail.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(lookupPatientByEmail.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to lookup patient by email';
            })
    },
});

export const { updatePatientChat } = patientSlice.actions;
export default patientSlice.reducer;